import { useT } from "../../../core/i18n";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Table from "../../../core/common/dataTable/index";
import {
  getAllAnnouncements,
  toggleFavourite,
} from "../../../core/data/redux/announcementSlice";
import { BASE_URLS } from "../../../core/data/redux/apis/urls";
import Api from "../../../core/data/redux/utils/apiCall";
import { DateFormat } from "../../../core/hooks/dateFormat";

interface Newsletter {
  id: number;
  title: string;
  category: string;
  description?: string;
  type: string;
  department?: string;
  designation?: string;
  isActive: boolean;
  isFavourite?: boolean;
  createdAt: string;
  updatedAt: string;
  image?: string;
  pdf?: string;
}

const NewslettersTab: React.FC = () => {
  const { t } = useT();
  const dispatch = useDispatch();
  const announcementState = useSelector(
    (state: any) => state.announcementState
  );
  const [dataList, setDataList] = useState<Newsletter[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Fetch newsletters data from API using Redux
  const fetchNewsletters = (page: number = 1, limit: number = 10) => {
    dispatch(
      getAllAnnouncements({
        page,
        limit,
        category: "News",
        isActive: true,
        orderBy: "DESC",
        fav: false,
      })
    );
  };

  // Load newsletters on component mount
  useEffect(() => {
    fetchNewsletters(pagination.current, pagination.pageSize);
  }, []);

  // Handle Redux state changes
  useEffect(() => {
    if (
      announcementState.loading === "fulfilled" &&
      announcementState.action === "list"
    ) {
      // Response structure: response.data.data.data contains the actual data
      const apiData = announcementState.data?.data?.data;
      if (apiData) {
        // Map the data and add isFavourite field based on fav object
        const mappedData = (apiData.data || apiData || []).map((item: any) => ({
          ...item,
          isFavourite: !!item.fav, // Convert fav object to boolean
        }));
        setDataList(mappedData);
        setPagination((prev) => ({
          ...prev,
          current: apiData.page || prev.current,
          pageSize: apiData.limit || prev.pageSize,
          total: apiData.total || 0,
        }));
      }
    } else if (
      announcementState.loading === "rejected" &&
      announcementState.action === "list"
    ) {
      toast.error(announcementState.error || "Failed to fetch newsletters");
    } else if (
      announcementState.loading === "fulfilled" &&
      announcementState.action === "favourite"
    ) {
      // Show dynamic message from API response
      const apiMessage = announcementState.favouriteData?.message;
      if (apiMessage) {
        if (apiMessage.toLowerCase().includes("added")) {
          toast.success("Newsletter added to favourites successfully!");
        } else if (apiMessage.toLowerCase().includes("removed")) {
          toast.success("Newsletter removed from favourites successfully!");
        } else {
          toast.success(apiMessage);
        }
      } else {
        toast.success("Favourite status updated successfully!");
      }
      // Reload the list immediately after toggling favourite
      fetchNewsletters(pagination.current, pagination.pageSize);
    } else if (
      announcementState.loading === "rejected" &&
      announcementState.action === "favourite"
    ) {
      toast.error(
        announcementState.error || "Failed to update favourite status"
      );
    }
  }, [announcementState.loading, announcementState.action]);

  // Toggle favourite status
  const handleToggleFavourite = (id: number) => {
    dispatch(toggleFavourite({ id }));
  };

  // Get presigned URL for file viewing/downloading
  const getPresignedUrl = async (key: string) => {
    try {
      // Check if it's already a full URL (starts with http/https)
      if (key.startsWith("http://") || key.startsWith("https://")) {
        return key; // Return as-is if it's already a full URL
      }

      console.log("Requesting presigned URL for key:", key);
      const endPoint = `${BASE_URLS.BASE_URL}aws-bucket/get-presigned-url`;
      const response = await Api.post(endPoint, { key });
      console.log("Presigned URL response:", response.data);
      return response.data;
    } catch (error: any) {
      console.error("Error getting presigned URL:", error);
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to get file URL. The file may not exist in storage.";
      toast.error(errorMessage);
      return null;
    }
  };

  // Handle file view/download
  const handleViewFile = async (key: string) => {
    if (!key) {
      toast.error("File not available");
      return;
    }

    const presignedUrl = await getPresignedUrl(key);
    if (presignedUrl) {
      window.open(presignedUrl, "_blank");
    }
  };

  // Table columns definition
  const columns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      width: 100,
      render: (text: string, record: any, index: number) => (
        <div>
          {pagination.current
            ? index +
              pagination.current * pagination.pageSize -
              pagination.pageSize +
              1
            : index + 1}
        </div>
      ),
    },
    {
      title: t("title"),
      dataIndex: "title",
      sorter: (a: any, b: any) => a.title.localeCompare(b.title),
      render: (text: string) => (
        <span
          className="text-truncate"
          style={{ maxWidth: "300px", display: "block" }}
        >
          {text}
        </span>
      ),
    },
    {
      title: t("type"),
      dataIndex: "type",
      sorter: (a: any, b: any) => (a.type || "").localeCompare(b.type || ""),
      render: (type: string) => type || "-",
    },
    {
      title: t("start_date"),
      dataIndex: "start_date",
      sorter: (a: any, b: any) => {
        const dateA = a.start_date ? new Date(a.start_date).getTime() : 0;
        const dateB = b.start_date ? new Date(b.start_date).getTime() : 0;
        return dateA - dateB;
      },
      render: (date: string) => DateFormat(date, "DD-MM-YYYY") || "-",
    },
    {
      title: t("end_date"),
      dataIndex: "end_date",
      sorter: (a: any, b: any) => {
        const dateA = a.end_date ? new Date(a.end_date).getTime() : 0;
        const dateB = b.end_date ? new Date(b.end_date).getTime() : 0;
        return dateA - dateB;
      },
      render: (date: string) => DateFormat(date, "DD-MM-YYYY") || "-",
    },
    {
      title: t("created_date"),
      dataIndex: "created_at",
      sorter: (a: any, b: any) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      render: (date: string) => DateFormat(date, "DD-MM-YYYY") || "-",
    },
    {
      title: t("status"),
      dataIndex: "isActive",
      render: (isActive: boolean) => (
        <span
          className={`badge ${
            isActive ? "badge-success" : "badge-danger"
          } badge-xs`}
        >
          {isActive ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      title: t("actions"),
      dataIndex: "actions",
      width: 150,
      render: (text: string, record: any) => (
        <div className="action-icon d-inline-flex">
          <Link
            to="#"
            className="me-2"
            onClick={(e) => {
              e.preventDefault();
              handleToggleFavourite(record.id);
            }}
          >
            <i
              className={
                record.isFavourite
                  ? "ti ti-bookmark-filled text-warning"
                  : "ti ti-bookmark"
              }
            />
          </Link>
          {record.pdf && (
            <Link
              to="#"
              className="me-2"
              onClick={(e) => {
                e.preventDefault();
                handleViewFile(record.pdf);
              }}
            >
              <i className="ti ti-file" />
            </Link>
          )}
        </div>
      ),
    },
  ];

  // Handle pagination change
  const handlePagination = (paginationData: any) => {
    setPagination({
      current: paginationData.current,
      pageSize: paginationData.pageSize,
      total: paginationData.total || 0,
    });
    fetchNewsletters(paginationData.current, paginationData.pageSize);
  };

  // Handle search
  const handleSearch = (value: string) => {
    // Implement search logic here if needed
    console.log("Search:", value);
  };

  const loading =
    announcementState.loading === "pending" &&
    announcementState.action === "list";

  return (
    <div className="card">
      <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
        <h4 className="mb-3">{t("newsletters")}</h4>
      </div>
      <div className="card-body p-0 py-3">
        <Table
          columns={columns}
          dataSource={dataList.map((item) => ({ ...item, key: item.id }))}
          Selection={false}
          paginationConfig={pagination}
          handlePagination={handlePagination}
          handleSearch={handleSearch}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default NewslettersTab;
